import { HeaderTypes, HttpClientHelper, PTVNClient } from '@ptvn-react/authentication';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import fileDownload from 'js-file-download';
import { omit } from 'lodash';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Category } from '../models/Category';
import { EMarketplaceEventAuthorization } from '../models/EMarketplaceEventHandler';
import { Sorting } from '../models/Model';
import {
  JobBoardListFilterData,
  StateListSupplierJobBoardList,
} from '../pages/JobBoard/JobBoardList/Model/Filter';
import { JobBoardListQuery } from '../pages/JobBoard/JobBoardList/Model/Query';
import APIConstant from '../utils/constants/APIConstant';
import { ConversationSortByEnum, ConversationType } from '../utils/constants/JobBoardConstants';
import { CommonHelper } from '../utils/helpers/CommonHelper';
import {
  CancelEasyQuoteBuyingLeadRequest,
  CreateBuyingLeadRequest,
  EditBuyingLeadRequest,
  EditConversationRequest,
  ExportSupplierTransactionReportRequest,
  GetJobConversationQuery,
  PostBuyerConversationRequest,
  PostSupplierConversationRequest,
  ProductShowCaseListRequest,
  SupplierBranchRequest,
  SupplierJobBoardListFilter,
  SupplierJobQuote,
  SupplierPostQuoteRequest,
  SupplierQuotaionRequest,
  SupplierRespondedHistoryBuyerRequest,
  SupplierRespondedHistorySupplierRequest,
  TrackingBuyerSearchRequest,
  BuyerEventRequest,
  SupplierOfferingBoardListFilter,
} from './request/EMarketplaceRequest';
import { PTVNResponse } from './response/BaseResponse';
import { GetCategoryResponseItem } from './response/EMarketplace';
import { CreateBuyingLeadItem } from './request/EMarketplaceRequest';
import { FileMIME } from '../models/File';
import {
  AttachmentResponse,
  BuyerCompanyDetail,
  BuyerJobDetailHistoryResponse,
  BuyerJobDetailResponse,
  CartAmount,
  CartProductItem,
  CartResponse,
  ConversationAttachment,
  ConversationPostHistory,
  ConversationReplyHistory,
  ConversationResponse,
  EasyQuoteVerifySupplier,
  EstimatedProjectValue,
  GetJobByErfxNoResponse,
  ProductDetailResponse,
  ProductShowCaseResponse,
  ShippingAreaResponse,
  SubCompanyListItemResponse,
  SubCompanyListResponse,
  SupplierAddCartResponse,
  SupplierCartResponse,
  SupplierDetail,
  SupplierJobDetailResponse,
  SupplierRecommendJobCategoryResponse,
  SupplierResponseBuyerList,
  SupplierResponseList,
  VerfiryCart,
  VerfiryCartRequest,
  VerfiryCartResponse,
  VerifySupplier,
  SupplierRespondedHistoryListResponse,
} from './response/EMarketplaceResponse';
import { SortDirectionEnum } from '../utils/constants/CommonConstants';
import { PIOfferingBoardQuery } from '../pages/PI/PIOfferingBoard/Model/PIOfferingBoardQuery';
import { OfferingBoardListQuery } from '../pages/PI/PIOfferingBoard/Model/Query';
import { OfferingBoardListFilterData } from '../pages/PI/PIOfferingBoard/Model/Filter';

export class EMarketplaceClient {
  public static getCategory(search?: string): Observable<Category[]> {
    const url = `${APIConstant.eMarketplace.getCategory.replace('{search}', search || '')}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get<PTVNResponse<GetCategoryResponseItem[]>>(url, options).pipe(
      map((m) => m.data.data.map((catItem) => new Category(catItem))),
    );
  }

  public static getCategories(search?: string): Observable<Category[]> {
    const url = `${APIConstant.eMarketplace.getCategories.replace('{search}', search || '')}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get<PTVNResponse<GetCategoryResponseItem[]>>(url, options).pipe(
      map((m) => m.data.data.map((catItem) => new Category(catItem))),
    );
  }

  public static getJobBoardList(
    companyID: number,
    filter: JobBoardListQuery,
    timeZone: string,
    lang: string,
  ): Observable<any> {
    let url = `${APIConstant.eMarketplace.getJobBoardList}`.replace(
      '{companyID}',
      companyID.toString(),
    );
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getJobBoardListFilterData(
    companyID: number,
    filter: Partial<JobBoardListQuery>,
    timeZone: string,
    lang: string,
  ): Observable<any> {
    let url = `${APIConstant.eMarketplace.getJobBoardList}-filter`.replace(
      '{companyID}',
      companyID.toString(),
    );
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getAdminJobBoardList(
    filter: JobBoardListQuery,
    timeZone: string,
    lang: string,
  ): Observable<any> {
    let url = APIConstant.eMarketplace.getAdminJobBoardList;
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getAdminJobBoardListFilterData(
    filter: Partial<JobBoardListQuery>,
    timeZone: string,
    lang: string,
  ): Observable<any> {
    let url = `${APIConstant.eMarketplace.getAdminJobBoardList}-filter`;
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static postBuyingLead(
    companyID: number,
    postPricePlanObj: CreateBuyingLeadRequest,
    files: File[],
  ): Observable<any> {
    const url = APIConstant.eMarketplace.postBuyingLead.replace(
      '{companyID}',
      companyID.toString(),
    );
    const payload = new FormData();
    payload.append(
      'job',
      new Blob([JSON.stringify(postPricePlanObj)], { type: 'application/json' }),
    );
    files.forEach((f) => {
      payload.append('file', f);
    });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static editBuyingLead(
    companyID: number,
    jobID: number,
    request: EditBuyingLeadRequest,
    newAttachments: File[],
  ): Observable<any> {
    const url =
      APIConstant.eMarketplace.postBuyingLead.replace('{companyID}', companyID.toString()) +
      `/${jobID.toString()}`;
    const payload = new FormData();
    payload.append('job', new Blob([JSON.stringify(request)], { type: 'application/json' }));
    newAttachments.forEach((a) => {
      payload.append('newAttachments', a);
    });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.put(url, payload, options).pipe(map((m) => m.data));
  }

  public static getJobDetail(
    companyID: number,
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<PTVNResponse<BuyerJobDetailResponse>> {
    const url = `${APIConstant.eMarketplace.getBuyerJobDetail}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static getAdminJobDetail(
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<PTVNResponse<BuyerJobDetailResponse>> {
    const url = `${APIConstant.eMarketplace.getAdminJobDetail}`.replace(
      '{jobID}',
      jobID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static getJobIdByERFxNo(
    companyID: number,
    erfxNo: number,
  ): Observable<PTVNResponse<GetJobByErfxNoResponse>> {
    const url = `${APIConstant.eMarketplace.getBuyerJobDetailByERFxNo}`
      .replace('{companyID}', companyID.toString())
      .replace('{erfxNo}', erfxNo.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static downloadJobAttachment(
    jobVersionID: number,
    attachment: AttachmentResponse,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadJobAttachment}`
      .replace('{jobID}', jobVersionID.toString())
      .replace('{attachmentID}', attachment.attachmentID.toString());
    return this.downloadFileWithAuth(url, attachment.originalFileName);
  }

  public static downloadJobResponseAttachment(
    jobResponseID: number,
    attachment: AttachmentResponse,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadJobResponseAttachment}`
      .replace('{jobResponseID}', jobResponseID.toString())
      .replace('{attachmentID}', attachment.attachmentID.toString());
    return this.downloadFileWithAuth(url, attachment.originalFileName);
  }

  public static downloadFileWithAuth(path: string, fileName: string): Observable<boolean> {
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(path, { ...options, responseType: 'blob' }).pipe(
      map((response) => {
        fileDownload(response.data, fileName);
        return true;
      }),
      catchError((err) => throwError(err)),
    );
  }

  public static getSupplierJobDetail(
    supplierID: number,
    branchID: number,
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<PTVNResponse<SupplierJobDetailResponse>> {
    const url = `${APIConstant.eMarketplace.getSupplierJobDetail}`
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static postQuote(
    supplierID: number,
    branchID: number,
    jobID: number,
    jobQuote: SupplierJobQuote,
    attachments: File[],
  ): Observable<any> {
    const url = APIConstant.eMarketplace.postQuote
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString());
    const body: SupplierPostQuoteRequest = {
      supplierJobQuote: new Blob([JSON.stringify(jobQuote)], {
        type: 'application/json',
      }),
    };
    const payload = CommonHelper.objectToFormData(body);
    for (const attachment of attachments) {
      payload.append('attachmentList', attachment);
    }
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static getSupplierJobBoardList(
    companyID: number,
    filter: Partial<SupplierJobBoardListFilter>,
    timeZone: string,
    lang: string,
    branchID: number,
  ): Observable<any> {
    let url = `${APIConstant.eMarketplace.getSupplierJobBoardList}`
      .replace('{companyID}', companyID.toString())
      .replace('{branchID}', branchID!.toString());
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierJobBoardListFilterData(
    companyID: number,
    filter: SupplierJobBoardListFilter,
    timeZone: string,
    lang: string,
    branchID: number,
  ): Observable<JobBoardListFilterData> {
    let url = `${APIConstant.eMarketplace.getSupplierJobBoardList}-filter`
      .replace('{companyID}', companyID.toString())
      .replace('{branchID}', branchID.toString());
    url += CommonHelper.objectToQueryString({ ...filter, timeZone, lang });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierOfferingBoardListFilterData(
    companyID: number,
    filter: SupplierOfferingBoardListFilter,
    timeZone: string,
    lang: string,
    branchID: number,
  ): Observable<OfferingBoardListFilterData> {
    let url = `${APIConstant.pi.getSupplierOfferingBoardFilterList}-filter`
      .replace('{companyID}', companyID.toString())
      .replace('{branchID}', branchID.toString());
    url += CommonHelper.objectToQueryString({ ...filter, timeZone, lang });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getBuyerOfferingBoardListFilterData(
    companyID: number,
    filter: SupplierOfferingBoardListFilter,
    timeZone: string,
    lang: string,
  ): Observable<OfferingBoardListFilterData> {
    let url = `${APIConstant.pi.getBuyerOfferingBoardFilterList}-filter`.replace(
      '{companyID}',
      companyID.toString(),
    );
    url += CommonHelper.objectToQueryString({ ...filter, timeZone, lang });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSubCompanyList(
    companyID: any,
  ): Observable<PTVNResponse<SubCompanyListResponse>> {
    const url = `${APIConstant.eMarketplace.getSubCompanyList}`.replace(
      '{companyID}',
      companyID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static cancelJob(companyID: number, jobID: number, reason: string): Observable<any> {
    const url = `${APIConstant.eMarketplace.cancelBuyingLead}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    const request: CancelEasyQuoteBuyingLeadRequest = {
      reason,
    };
    return PTVNClient.patch(url, request, options).pipe(map((m) => m.data));
  }

  public static createRFQAtErfx(jobID: number, companyID: number): Observable<any> {
    const url = `${APIConstant.eMarketplace.createRFQ}`;
    const payload = { jobID, companyID };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static getErfxBtnDetail() {
    const url = `${APIConstant.eMarketplace.getErfxBtn}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getProductDetail(
    productID: number,
    currencyCode: string,
  ): Observable<ProductDetailResponse> {
    const url = `${APIConstant.eMarketplace.productDetail}`
      .replace('{productID}', String(productID))
      .replace('{currencyCode}', currencyCode);
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getProductCart(currencyCode: string): Observable<CartResponse> {
    const url = `${APIConstant.eMarketplace.productCart}?currencyCode=${currencyCode}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static addCart(productID: number, quantity: number): Observable<CartProductItem> {
    const url = `${APIConstant.eMarketplace.productCart}`;
    const payload = { productID, quantity };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data.data));
  }
  public static addSupplierCart(
    supplierID: number,
    branchID: number,
    eRFxNo: number | null,
  ): Observable<SupplierAddCartResponse> {
    const url = `${APIConstant.eMarketplace.getSupplierCart}`;
    const payload = { supplierID, branchID, eRFxNo };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data.data));
  }

  public static updateProductQuantity(
    productCartItemID: number,
    quantity: number,
  ): Observable<any> {
    const url = `${APIConstant.eMarketplace.productDetailCartItem}`;
    const payload = { productCartItemID, quantity };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.patch(url, payload, options).pipe(map((m) => m.data.data));
  }

  public static deleteCartProductItem(productCartItemIDList: number[]): Observable<any> {
    const url = `${APIConstant.eMarketplace.productDetailCartItem}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { productCartItemIDs: productCartItemIDList.toString() };
    return PTVNClient.delete(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierCart(): Observable<SupplierCartResponse> {
    const url = `${APIConstant.eMarketplace.getSupplierCart}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static deleteSupplierCartItem(supplierCartItemIDList: number[]): Observable<any> {
    const url = `${APIConstant.eMarketplace.supplierCartItem}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { supplierCartItemIDs: supplierCartItemIDList.join(',') };
    return PTVNClient.delete(url, options).pipe(map((m) => m.data.data));
  }

  public static deleteSupplierCartItemByCompanyIDAndBranchID(
    companyID: number,
    branchID: number,
  ): Observable<any> {
    const url = `${APIConstant.eMarketplace.supplierInfo}`
      .replace('{companyID}', String(companyID))
      .replace('{branchID}', String(branchID));
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.delete(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierInfo(companyID: number, branchID: number): Observable<SupplierDetail> {
    const url = `${APIConstant.eMarketplace.supplierInfo}`
      .replace('{companyID}', String(companyID))
      .replace('{branchID}', String(branchID));
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static createProductQuotation(
    productIDs: number[],
    quantity: any,
    currency: string,
    lang: string,
  ): Observable<any> {
    const url = `${APIConstant.eMarketplace.createProductQuotation}`;
    const payload = { productIDs, currency, lang, quantity };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static updateProductQuotation(
    productIDs: number[],
    eRFxNo: number,
    quantity: number | undefined,
    lang: string,
  ): Observable<any> {
    const url = `${APIConstant.eMarketplace.updateProductQuotation}`;
    const payload = { productIDs, eRFxNo, lang, quantity };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static createSupplierQuotation(payload: SupplierQuotaionRequest): Observable<any> {
    const url = `${APIConstant.eMarketplace.createSupplierQuotation}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static updateSupplierQuotation(
    suppliers: SupplierBranchRequest[],
    eRFxNo: number,
  ): Observable<any> {
    const url = `${APIConstant.eMarketplace.updateSupplierQuotation}`;
    const payload = { suppliers, eRFxNo };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static verifySupplierCart(suppliers: VerfiryCart[]): Observable<VerfiryCartResponse> {
    const request: VerfiryCartRequest = { suppliers };
    const url = `${APIConstant.eMarketplace.verifySupplierCart}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static getCartAmount(): Observable<CartAmount> {
    const url = `${APIConstant.eMarketplace.cartAmount}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierResponseListBySupplierID(
    supplierID: number,
    jobID: number,
    branchID: number,
    page: number,
    pageSize: number,
  ): Observable<SupplierResponseList> {
    const url = `${APIConstant.eMarketplace.supplierResponses
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { jobID, page, pageSize };
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static buyerGetSupplierResponseListBySupplierUserID(
    companyID: number,
    supplierID: number,
    jobID: number,
    branchID: number,
    userID: number,
    page: number,
    pageSize: number,
  ): Observable<SupplierResponseList> {
    const url = `${APIConstant.eMarketplace.supplierResponsesByUser
      .replace('{companyID}', companyID.toString())
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{userID}', userID.toString())}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { jobID, page, pageSize };
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static adminGetSupplierResponseListBySupplierUserID(
    supplierID: number,
    jobID: number,
    branchID: number,
    userID: number,
    page: number,
    pageSize: number,
  ): Observable<SupplierResponseList> {
    const url = `${APIConstant.eMarketplace.getAdminSupplierResponsesByUser
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{userID}', userID.toString())}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { jobID, page, pageSize };
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierResponseListBycompanyIDAndJobID(
    companyID: number,
    jobID: number,
  ): Observable<SupplierResponseBuyerList> {
    const url = `${APIConstant.eMarketplace.supplierResponsesBuyer
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString())}`;

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierResponseListByJobIDForAdmin(
    jobID: number,
  ): Observable<SupplierResponseBuyerList> {
    const url = `${APIConstant.eMarketplace.getAdminSupplierResponses.replace(
      '{jobID}',
      jobID.toString(),
    )}`;

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static invokeEventHandlerAuthorization(): Observable<EMarketplaceEventAuthorization> {
    const authURL = `${APIConstant.eventHandler.authorization}`;

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(authURL, options).pipe(
      take(1),
      map((res) => res.data.data),
    );
  }

  public static getProductShowCase(companyID: number): Observable<ProductShowCaseResponse[]> {
    const url = `${APIConstant.eMarketplace.supplierProductShowCase}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { companyID };
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static upsertProductShowCase(
    request: ProductShowCaseListRequest,
  ): Observable<ProductShowCaseResponse[]> {
    const url = `${APIConstant.eMarketplace.supplierProductShowCase}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierRecommendJobCategory(
    companyID: number,
    branchID: number,
  ): Observable<SupplierRecommendJobCategoryResponse> {
    let url = `${APIConstant.eMarketplace.supplierRecommendJobCategory}`
      .replace('{supplierID}', companyID.toString())
      .replace('{branchID}', branchID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getShippingAreaOptionList(): Observable<ShippingAreaResponse[]> {
    const url = `${APIConstant.eMarketplace.shippingArea}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getEstimatedProjectValueOptionList(
    currency: string,
  ): Observable<EstimatedProjectValue[]> {
    const url = `${APIConstant.eMarketplace.estimatedProjectValue}/${currency}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getStateListSupplierJobBoardList(
    supplierID: number,
    branchID: number,
    jobIDs: number[],
  ): Observable<StateListSupplierJobBoardList> {
    let url = APIConstant.eMarketplace.getStateListSupplierJobBoardList
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, { jobIDs }, options).pipe(map((m) => m.data.data));
  }

  public static getStateListBuyerJobBoardList(
    companyID: number,
    jobIDs: number[],
  ): Observable<StateListSupplierJobBoardList> {
    let url = APIConstant.eMarketplace.getStateListBuyerJobBoardList.replace(
      '{companyID}',
      companyID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, { jobIDs }, options).pipe(map((m) => m.data.data));
  }

  public static eRFxVerifySupplier(
    supplierID: number,
    branchID: number,
    erfxNo: number,
  ): Observable<VerifySupplier> {
    const url = `${APIConstant.eMarketplace.verifySupplier}`
      .replace('{erfxNo}', erfxNo.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{supplierID}', supplierID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static easyQuoteVerifySupplier(
    supplierID: number,
    branchID: number,
    jobID: number,
  ): Observable<EasyQuoteVerifySupplier> {
    const url = `${APIConstant.eMarketplace.easyQuoteverifySupplier}`
      .replace('{jobID}', jobID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{supplierID}', supplierID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static exportSupplierTransactionReport(
    supplierID: number,
    request: ExportSupplierTransactionReportRequest,
    timeZone: string,
  ): Observable<File> {
    const url =
      APIConstant.eMarketplace.exportSupplierTransactionReport.replace(
        '{supplierID}',
        supplierID.toString(),
      ) + CommonHelper.objectToQueryString({ ...request, timeZone });
    let options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options = { ...options, responseType: 'blob' };
    return PTVNClient.get(url, options).pipe(
      map((response) => {
        return response.data;
      }),
    );
  }

  public static getBuyerCompanyDetail(companyID: number): Observable<BuyerCompanyDetail> {
    const url = `${APIConstant.eMarketplace.getBuyerCompany}`.replace(
      '{companyID}',
      companyID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static exportBuyingLeadReport(
    companyID: number,
    jobID: number,
  ): Observable<AxiosResponse<any>> {
    const url = `${APIConstant.report.exportBuyingLeads}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, { responseType: 'blob', ...options }).pipe(map((m) => m));
  }

  public static buyerSaveFavoriteJob(
    companyID: number,
    jobID: number,
  ): Observable<AxiosResponse<any>> {
    const url = `${APIConstant.eMarketplace.buyerSaveJobFavorite}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.put(url, undefined, options).pipe(map((m) => m));
  }

  public static supplierSaveFavoriteJob(
    companyID: number,
    branchID: number,
    jobID: number,
  ): Observable<AxiosResponse<any>> {
    const url = `${APIConstant.eMarketplace.supplierSaveJobFavorite}`
      .replace('{supplierID}', companyID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.put(url, undefined, options).pipe(map((m) => m));
  }

  public static getBuyerSubCompany(
    companyID: any,
  ): Observable<PTVNResponse<SubCompanyListItemResponse>> {
    const url = `${APIConstant.eMarketplace.getBuyerSubCompany}`.replace(
      '{companyID}',
      companyID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static postBuyerJobConversation(
    companyID: number,
    jobID: number,
    request: PostBuyerConversationRequest,
  ): Observable<AxiosResponse<any>> {
    const conversationType = request.conversationType;
    let url = `${APIConstant.eMarketplace.postBuyerConversation}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType);

    if (conversationType === 'reply') {
      url += `/${request.conversationPostID?.toString()}`;
    }

    const payload = CommonHelper.objectToFormData(
      omit(request, ['conversationType', 'attachments', 'conversationPostID']),
    );
    request.attachments?.forEach((a) => {
      payload.append('attachments', a);
    });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m));
  }

  public static editBuyerJobConversation(
    companyID: number,
    jobID: number,
    request: EditConversationRequest,
  ): Observable<AxiosResponse<any>> {
    const conversationType = request.conversationType;
    let url = `${APIConstant.eMarketplace.editBuyerConversation}/${(conversationType === 'reply'
      ? request.replyID
      : request.conversationPostID
    )?.toString()}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType === 'reply' ? 'reply' : 'post/conversation');

    const payload = CommonHelper.objectToFormData(
      omit(request, ['conversationType', 'attachments', 'conversationPostID']),
    );
    request.attachments?.forEach((a) => {
      payload.append('attachments', a);
    });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m));
  }

  public static getBuyerJobConversationHistory(
    companyID: number,
    jobID: number,
    conversationType: string,
    conversationPostID?: number,
    replyID?: number,
  ): Observable<PTVNResponse<ConversationPostHistory | ConversationReplyHistory>> {
    let url = `${APIConstant.eMarketplace.editBuyerConversation}/${(conversationType === 'reply'
      ? replyID
      : conversationPostID
    )?.toString()}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType === 'reply' ? 'reply' : 'conversation');

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getAdminJobConversationHistory(
    jobID: number,
    conversationType: string,
    conversationPostID?: number,
    replyID?: number,
  ): Observable<PTVNResponse<ConversationPostHistory | ConversationReplyHistory>> {
    let url = `${APIConstant.eMarketplace.getAdminConversationHistory}/${(conversationType ===
    'reply'
      ? replyID
      : conversationPostID
    )?.toString()}`
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType === 'reply' ? 'reply' : 'conversation');

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static postSupplierJobConversation(
    supplierID: number,
    branchID: number,
    jobID: number,
    request: PostSupplierConversationRequest,
  ): Observable<AxiosResponse<any>> {
    const conversationType = request.conversationType;
    let url = `${APIConstant.eMarketplace.postSupplierConversation}`
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType);

    if (conversationType === 'reply') {
      url += `/${request.conversationPostID?.toString()}`;
    }

    const payload = CommonHelper.objectToFormData(
      omit(request, ['conversationType', 'attachments', 'conversationPostID']),
    );
    request.attachments?.forEach((a) => {
      payload.append('attachments', a);
    });

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m));
  }

  public static editSupplierJobConversation(
    supplierID: number,
    branchID: number,
    jobID: number,
    request: EditConversationRequest,
  ): Observable<AxiosResponse<any>> {
    const conversationType = request.conversationType;
    let url = `${APIConstant.eMarketplace.editSupplierConversation}/${(conversationType === 'reply'
      ? request.replyID
      : '/conversation/' + request.conversationPostID
    )?.toString()}`
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType);

    const payload = CommonHelper.objectToFormData(
      omit(request, ['conversationType', 'attachments', 'conversationPostID']),
    );
    request.attachments?.forEach((a) => {
      payload.append('attachments', a);
    });
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m));
  }

  public static getSupplierJobConversationHistory(
    supplierID: number,
    branchID: number,
    jobID: number,
    conversationType: ConversationType,
    conversationPostID?: number,
    replyID?: number,
  ): Observable<AxiosResponse<any>> {
    let url = `${APIConstant.eMarketplace.editSupplierConversation}/${(conversationType === 'reply'
      ? replyID
      : conversationPostID
    )?.toString()}`
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType === 'reply' ? 'reply' : 'conversation');

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static downloadBuyerConversationAttachment(
    companyID: number,
    jobID: number,
    attachment: ConversationAttachment,
    conversationType: ConversationType,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadBuyerConversationAttachment}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType)
      .replace('{attachmentID}', attachment.attachmentID.toString());

    return this.downloadFileWithAuth(url, attachment.originalFileName);
  }

  public static downloadSupplierConversationAttachment(
    supplierID: number,
    branchID: number,
    jobID: number,
    attachment: ConversationAttachment,
    conversationType: ConversationType,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadSupplierConversationAttachment}`
      .replace('{supplier}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString())
      .replace('{conversationType}', conversationType)
      .replace('{attachmentID}', attachment.attachmentID.toString());

    return this.downloadFileWithAuth(url, attachment.originalFileName);
  }

  public static downloadAdminConversationAttachment(
    attachment: ConversationAttachment,
    conversationType: ConversationType,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadAdminConversationAttachment}`
      .replace('{conversationType}', conversationType)
      .replace('{attachmentID}', attachment.attachmentID.toString());

    return this.downloadFileWithAuth(url, attachment.originalFileName);
  }

  public static getBuyerJobConversation(
    companyID: number,
    jobID: number,
    query: GetJobConversationQuery,
  ): Observable<PTVNResponse<ConversationResponse>> {
    const url =
      `${APIConstant.eMarketplace.getBuyerJobConversation}`
        .replace('{companyID}', companyID.toString())
        .replace('{jobID}', jobID.toString()) + CommonHelper.objectToQueryString(query);

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static getSupplierJobConversation(
    supplierID: number,
    branchID: number,
    jobID: number,
    query: Sorting<ConversationSortByEnum, SortDirectionEnum>,
  ): Observable<PTVNResponse<ConversationResponse>> {
    const url =
      `${APIConstant.eMarketplace.getSupplierJobConversation}`
        .replace('{supplierID}', supplierID.toString())
        .replace('{branchID}', branchID.toString())
        .replace('{jobID}', jobID.toString()) + CommonHelper.objectToQueryString(query);

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static getAdminJobConversation(
    jobID: number,
    query: GetJobConversationQuery,
  ): Observable<PTVNResponse<ConversationResponse>> {
    const url =
      `${APIConstant.eMarketplace.getAdminJobConversation}`.replace('{jobID}', jobID.toString()) +
      CommonHelper.objectToQueryString(omit(query, ['noReply']));

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static checkTutorial(type: string): Observable<PTVNResponse<any>> {
    const url = `${APIConstant.eMarketplace.tutorial}?type=${type}`;

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static postTutorial(type: string, status: string): Observable<PTVNResponse<any>> {
    const url = `${APIConstant.eMarketplace.tutorial}`;
    const request = {
      type,
      status,
    };

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, request, options).pipe(map((m) => m.data));
  }

  public static getCreateBuyingLeadUOMList(companyID: number): Observable<string[]> {
    const url = `${APIConstant.eMarketplace.getAddingBuyingLeadItemUOM}`.replace(
      '{companyID}',
      companyID.toString(),
    );

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static downloadJobItemDataAsExcel(
    companyID: number,
    items: CreateBuyingLeadItem[] | undefined,
  ): Observable<boolean> {
    const url = `${APIConstant.eMarketplace.downloadJobItemData}`.replace(
      '{companyID}',
      companyID.toString(),
    );

    const request = { items };

    const options: AxiosRequestConfig = {
      ...HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN),
      responseType: 'blob',
    };

    return PTVNClient.post(url, request, options).pipe(
      map((response) => {
        const fileName = response.headers['content-disposition'].split('filename=')[1];
        fileDownload(response.data, fileName, FileMIME.XLSX);
        return true;
      }),
      catchError((err) => throwError(err)),
    );
  }

  public static uploadItemDataAsExcel(
    companyID: number,
    file: File,
  ): Observable<Pick<CreateBuyingLeadRequest, 'items'>> {
    const url = APIConstant.eMarketplace.uploadJobItemData.replace(
      '{companyID}',
      companyID.toString(),
    );

    const payload = new FormData();
    payload.append('file', file);

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data.data));
  }

  public static getJobSupplierRespondedHistoryForBuyer(
    companyID: number,
    request: SupplierRespondedHistoryBuyerRequest,
  ): Observable<SupplierRespondedHistoryListResponse> {
    const url = APIConstant.eMarketplace.getjobSupplierRespondedHistoryBuyer.replace(
      '{companyID}',
      companyID.toString(),
    );

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static getJobSupplierRespondedHistoryForSupplier(
    supplierID: number,
    branchID: number,
    request: SupplierRespondedHistorySupplierRequest,
  ): Observable<SupplierRespondedHistoryListResponse> {
    const url = APIConstant.eMarketplace.getjobSupplierRespondedHistorySupplier
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString());

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static getJobSupplierRespondedHistoryForAdmin(
    request: SupplierRespondedHistoryBuyerRequest,
  ): Observable<SupplierRespondedHistoryListResponse> {
    const url = APIConstant.eMarketplace.getjobSupplierRespondedHistoryAdmin;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static updateBuyingLeadsPeriod(
    companyID: number,
    jobID: number,
    expireDate: number,
    openDate?: number,
  ): Observable<PTVNResponse<any>> {
    const url = APIConstant.eMarketplace.updateBuyingLeadsPeriod
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const request = {
      openDate,
      expireDate,
    };

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.put(url, request, options).pipe(map((m) => m.data));
  }

  public static buyerEditJobConversationAllowance(
    companyID: number,
    jobID: number,
    allowance: boolean,
  ): Observable<AxiosResponse<any>> {
    const url = `${APIConstant.eMarketplace.editJobConversationAllowance}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { allowance };

    return PTVNClient.put(url, undefined, options).pipe(map((m) => m));
  }

  public static getBuyerJobDetailHistory(
    companyID: number,
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<BuyerJobDetailHistoryResponse> {
    const url = `${APIConstant.eMarketplace.getBuyerJobDetailHistory}`
      .replace('{companyID}', companyID.toString())
      .replace('{jobID}', jobID.toString());

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getAdminJobDetailHistory(
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<BuyerJobDetailHistoryResponse> {
    const url = `${APIConstant.eMarketplace.getAdminJobDetailHistory}`.replace(
      '{jobID}',
      jobID.toString(),
    );

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getSupplierJobDetailHistory(
    supplierID: number,
    branchID: number,
    jobID: number,
    lang: string,
    currency: string,
  ): Observable<BuyerJobDetailHistoryResponse> {
    const url = `${APIConstant.eMarketplace.getSupplierJobDetailHistory}`
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{jobID}', jobID.toString());

    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options.params = { lang, currency };

    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static trackBuyerSearchingEvent(
    companyID: number,
    request: TrackingBuyerSearchRequest,
  ): Observable<any> {
    const url = APIConstant.eMarketplace.trackBuyerSearchingEvent.replace(
      '{companyID}',
      companyID.toString(),
    );
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, request, options).pipe(map((m) => m.data));
  }

  public static supplierEvent(
    supplierID: number,
    branchID: number,
    jobID: number,
    event: string,
  ): Observable<any> {
    const url = APIConstant.eMarketplace.supplierEvent
      .replace('{supplierID}', supplierID.toString())
      .replace('{branchID}', branchID.toString())
      .replace('{event}', event);
    const payload = { jobID };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static buyerEvent(
    companyID: number,
    event: string,
    itemType: string,
    payload: BuyerEventRequest,
  ): Observable<any> {
    const url = APIConstant.eMarketplace.buyerEvent
      .replace('{companyID}', companyID.toString())
      .replace('{event}', event)
      .replace('{itemType}', itemType);
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static getOfferingBoardList(
    companyID: number,
    filter: OfferingBoardListQuery,
    timeZone: string,
    lang: string,
    branchID: number,
  ): Observable<any> {
    let url = `${APIConstant.pi.getSupplierOfferingBoardFilterList}`
      .replace('{companyID}', companyID?.toString())
      .replace('{branchID}', branchID!?.toString());
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getBuyerOfferingBoardList(
    companyID: number,
    filter: OfferingBoardListQuery,
    timeZone: string,
    lang: string,
  ): Observable<any> {
    let url = `${APIConstant.pi.getBuyerOfferingBoardFilterList}`.replace(
      '{companyID}',
      companyID?.toString(),
    );
    url += `${CommonHelper.objectToQueryString({ ...filter, timeZone, lang })}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }
}
